import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { verifyEmailApi } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  LoaderIcon,
  AlertCircleIcon,
  MailCheckIcon,
} from 'lucide-react'

export const Route = createFileRoute('/verify-email')({
  validateSearch: (search: Record<string, string | undefined>): { token?: string } => ({
    token: search.token,
  }),
  component: () => {
    const { token } = Route.useSearch()
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
      token ? 'loading' : 'error',
    )
    const [errorMessage, setErrorMessage] = useState('')

    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
      if (!token) {
        setErrorMessage('No verification token provided.')
        return
      }

      let cancelled = false

      const verify = async () => {
        try {
          await verifyEmailApi(token)
          if (!cancelled) setStatus('success')
        } catch (err) {
          if (!cancelled) {
            setErrorMessage(err instanceof Error ? err.message : 'Verification failed')
            setStatus('error')
          }
        }
      }

      verify()

      return () => { cancelled = true }
    }, [token])

    useEffect(() => {
      if (status === 'success' && successHeadingRef.current) {
        successHeadingRef.current.focus()
      }
    }, [status])

    return (
      <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Decorative glow orbs */}
        <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] rounded-full bg-[#00E5A0]/5 blur-[80px]" />

        {/* Brand lockup */}
        <div className="relative z-10 flex items-center justify-center pt-12 sm:pt-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
              PP
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
          </Link>
        </div>

        {/* Verification result */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16">
          <div className="w-full max-w-md">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50 text-center">
              <span
                aria-hidden="true"
                className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                style={{ backgroundColor: BRAND_MINT }}
              />

              {status === 'loading' && (
                <div className="flex flex-col items-center py-4">
                  <LoaderIcon className="size-10 text-[#00E5A0] animate-spin" />
                  <p className="mt-5 text-sm text-white/60">Verifying your email…</p>
                </div>
              )}

              {status === 'success' && (
                <div className="flex flex-col items-center">
                  <div className="flex size-14 items-center justify-center rounded-full bg-[#00E5A0]/15">
                    <MailCheckIcon className="size-7 text-[#00E5A0]" />
                  </div>
                  <h1
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className="mt-5 font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-medium tracking-tight text-white outline-none"
                  >
                    Email verified successfully
                  </h1>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xs">
                    Your email address has been confirmed. You can now log in and access all PeerPass features.
                  </p>
                  <Link
                    to="/login"
                    className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base px-6 py-3 shadow-xl shadow-[#00E5A0]/25 transition-all"
                  >
                    Log in
                    <ArrowLeftIcon className="size-4 rotate-180" />
                  </Link>
                </div>
              )}

              {status === 'error' && (
                <div className="flex flex-col items-center">
                  <div className="flex size-14 items-center justify-center rounded-full bg-red-500/15">
                    <AlertCircleIcon className="size-7 text-red-400" />
                  </div>
                  <h1 className="mt-5 font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-medium tracking-tight text-white">
                    Verification failed
                  </h1>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xs">
                    {errorMessage || 'This verification link is invalid or has expired.'}
                  </p>
                  <div className="mt-6 flex flex-col items-center gap-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1.5 text-sm text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 transition-colors"
                    >
                      <ArrowLeftIcon className="size-4" />
                      Back to log in
                    </Link>
                    <p className="text-xs text-white/40">
                      Need a new verification link?{' '}
                      <Link
                        to="/login"
                        className="text-[#00E5A0] hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                      >
                        Log in to request one
                      </Link>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div className="relative z-10 flex justify-center pb-8">
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              Employer-paid benefit
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              0% interest
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              100% anonymous
            </span>
          </div>
        </div>
      </div>
    )
  },
})
